import { memo, useMemo } from 'react';
import { useRouter } from 'next/router';

import { Alert, TextButton } from '@zillow/constellation-10';

import { useRentalManagerUserFreemiumWebApiHandler_isTopOfFunnelEnrollmentEligible } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import accessUtils from '../../../utils/accessUtils';
import numberUtils from '../../../utils/numberUtils';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../constants/FreemiumConstants';

const PhoneNumberDisplayInfo = () => {
  const router = useRouter();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const activeProductType = listingDetails?.freemium?.activeProduct?.type;
  const isFreemiumEnabled = listingDetails?.freemium?.isEnabled ?? false;
  const isHidePhoneRestricted = listingDetails?.restrictions?.isHidePhoneRestricted;
  const trackingPhoneNumber = listingDetails?.trackingPhoneNumber;
  const isActiveProductTypePremium = activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM;

  const { data: isTopOfFunnelEnrollmentEligible = false } =
    useRentalManagerUserFreemiumWebApiHandler_isTopOfFunnelEnrollmentEligible({
      enabled: !isActiveProductTypePremium,
      meta: { tags: ['isTopOfFunnelEnrollmentEligible'], context: { api: 'freemium', details: 'ListingPage' } },
    });

  const canAccessTopOfFunnelPremium = accessUtils.canAccessTopOfFunnelPremium({
    isFreemiumEnabled,
    isTopOfFunnelEnrollmentEligible,
    isActiveProductTypePremium,
  });

  const content = useMemo(() => {
    const showPremiumUpsell = canAccessTopOfFunnelPremium && !!isHidePhoneRestricted;

    if (trackingPhoneNumber) {
      return `The phone number displayed to renters is ${numberUtils.formatPhoneNumberInput(trackingPhoneNumber)}.
      This allows us to better protect your identity and reduce spam. `;
    } else if (showPremiumUpsell) {
      return 'Phone numbers will not display on free listings';
    }
  }, [trackingPhoneNumber, isHidePhoneRestricted, canAccessTopOfFunnelPremium]);

  return (
    Boolean(content) && (
      <>
        <Alert
          data-testid="PhoneNumberDisplayInfoContainer"
          className="fs-mask"
          tone="info"
          actionButton={
            trackingPhoneNumber && (
              <TextButton aria-labelledby="learn-more-about-different-phone-number-alert" asChild>
                <a
                  target="_blank"
                  href="https://www.zillow.help/article/why-is-a-different-phone-number-showing-on-my-rental-listing-zd30237373747987"
                  rel="noopener noreferrer"
                >
                  Learn more
                </a>
              </TextButton>
            )
          }
        >
          {content}
        </Alert>
      </>
    )
  );
};

export default memo(PhoneNumberDisplayInfo);
